import { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import {
  Box,
  Divider,
  List,
  ListItem,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { CaseTypeEnum } from 'src/types';
import { InviteUserDropdownOption } from '../InviteToCaseTeamDrawer';
import { UserChips } from '../components/UserChips';

interface SummaryStepProps {
  caseType?: CaseTypeEnum;
  selectedPermissions: string[];
  selectedSuperAdmins: InviteUserDropdownOption[];
  selectedApprovers: InviteUserDropdownOption[];
  handleDeleteUserChip: (user: InviteUserDropdownOption) => void;
  note: string;
  handleNoteChange: (note: string) => void;
}

export const SummaryStep = ({
  caseType,
  selectedPermissions,
  selectedSuperAdmins,
  selectedApprovers,
  handleDeleteUserChip,
  note,
  handleNoteChange,
}: SummaryStepProps) => {
  const { tokens, presets } = useTheme<LensTheme>();
  const sp = tokens.semantic.spacing.desktop.gap;
  const { AtlasListItemText } = presets.ListPresets.components;

  const defaultPermissions = useMemo(
    () => ({
      caseDetails: {
        permission: 'No access',
        header: 'Case Details',
        display: caseType === CaseTypeEnum.SPEAK_UP,
      },
      casePII: {
        permission: 'Hide',
        header: 'Case PII',
        display: caseType === CaseTypeEnum.SPEAK_UP,
      },
      reportData: {
        permission: 'No access',
        header: 'Report Data',
        display: caseType === CaseTypeEnum.SPEAK_UP,
      },
      reportPII: {
        permission: 'Hide',
        header: 'Report PII',
        display: caseType === CaseTypeEnum.SPEAK_UP,
      },
      ReportAndAudit: {
        permission: 'No access',
        header: 'Report and audit log tab',
        display: caseType === CaseTypeEnum.COI,
      },
      reporterMessages: {
        permission: 'No access',
        header: 'Reporter Messages',
        display: true,
      },
      auditLogs: {
        permission: 'No access',
        header: 'Audit Logs',
        display: caseType === CaseTypeEnum.SPEAK_UP,
      },
      workspace: {
        permission: 'No access',
        header: 'Workspace',
        display: true,
      },
    }),
    [caseType],
  );

  const [permissions, setPermissions] = useState(defaultPermissions);

  const displayPermissionAccess = (permission: string) => {
    return permission.includes('VIEW') ? 'Read-only' : 'Editor';
  };

  useEffect(() => {
    const newPermissions = { ...defaultPermissions };

    const updatePermission = (accessType: string) => {
      switch (accessType) {
        case 'VIEW_CASE_DETAILS':
        case 'EDIT_CASE_DETAILS':
          newPermissions.caseDetails.permission =
            displayPermissionAccess(accessType);
          newPermissions.ReportAndAudit.permission =
            displayPermissionAccess(accessType);
          break;
        case 'VIEW_CASE_DETAILS_PII':
          newPermissions.casePII.permission = accessType.includes('VIEW')
            ? 'Show'
            : 'Hide';
          break;
        case 'VIEW_REPORT_DETAILS':
        case 'EDIT_REPORT_DETAILS':
          newPermissions.reportData.permission =
            displayPermissionAccess(accessType);
          break;
        case 'VIEW_REPORT_DETAILS_PII':
          newPermissions.reportPII.permission = accessType.includes('VIEW')
            ? 'Show'
            : 'Hide';
          break;
        case 'VIEW_CASE_AUDIT_LOGS_TAB':
        case 'EDIT_CASE_AUDIT_LOGS_TAB':
          newPermissions.auditLogs.permission =
            displayPermissionAccess(accessType);
          break;
        case 'VIEW_CASE_WORKSPACE_TAB':
        case 'EDIT_CASE_WORKSPACE_TAB':
          newPermissions.workspace.permission =
            displayPermissionAccess(accessType);
          break;
        case 'SEND_REPORT_MESSAGES':
        case 'VIEW_REPORT_MESSAGES':
          newPermissions.reporterMessages.permission =
            displayPermissionAccess(accessType);
          break;
        default:
          break;
      }
    };

    selectedPermissions.forEach((caseAccess) => {
      updatePermission(caseAccess);
    });

    setPermissions(newPermissions);
  }, [defaultPermissions, selectedPermissions]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: sp[3].value }}>
      <UserChips
        selectedSuperAdmins={selectedSuperAdmins}
        selectedApprovers={selectedApprovers}
        handleDeleteUserChip={handleDeleteUserChip}
      />
      <Divider />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: sp[1].value }}>
        <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
          Invitation note
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Enhance the invite by adding a note to provide context for the
          invitation. This will be included in their invite notification.
        </Typography>
        <TextField
          variant="outlined"
          placeholder="Add a note"
          multiline
          rows={4}
          value={note}
          onChange={(e) => handleNoteChange(e.target.value)}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          borderRadius: tokens.semantic.radius.lg.value,
          background: tokens.semantic.color.surface.variant.value,
          gap: sp[3].value,
          padding: sp[3].value,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            borderRadius: tokens.semantic.radius.lg.value,
            background: tokens.semantic.color.background.base.value,
            padding: sp[2].value,
            width: '100%',
            justifyContent: 'space-between',
          }}
        >
          <List>
            {Object.entries(permissions)
              .filter(([_key, value]) => value.display)
              .map(([key, value]) => (
                <ListItem key={key} dense>
                  <AtlasListItemText
                    primary={value.header}
                    secondary={value.permission}
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: sp[1].value,
                    }}
                  />
                </ListItem>
              ))}
          </List>
        </Box>
      </Box>
    </Box>
  );
};
